/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Heading } from '@msdyn365-commerce-modules/utilities';
import { Image, RichTextComponent } from '@msdyn365-commerce/core';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { FormInputGroupBuilder } from '../../components/input-group-form';
import { createEmployeeValidationInput, employeeValidationAction } from '../../dataActions/validation-form-employee';
import validateFriendsAndFamilyAction, { createValidateFriendsAndFamilyInput } from '../family-and-friends/actions/validate-friends-and-family.action';
import { IValidationFormProps } from './validation-form.props.autogenerated';

export interface IValidationFormViewProps extends IValidationFormProps<{}> {
}

type validationFormData = {
    employeeNumber: string;
    lastName: string;
    birthYear: string;
};

/**
 *
 * ValidationForm component
 * @extends {React.Component<IValidationFormProps<IValidationFormData>>}
 */

@observer
class ValidationForm extends React.Component<IValidationFormProps<{}>> {

    @observable private _isFormCompleted: boolean = false;

    @observable private _displayValidationError: boolean = false;
    @observable private _displayCommunicationsError: boolean = false;

    @observable private _ffDisplayValidationError: boolean = false;
    @observable private _ffDisplayCommunicationsError: boolean = false;

    public constructor(props: IValidationFormProps<{}>) {
        super(props);

        this._onFinished = this._onFinished.bind(this);
        this._onFriendsAndFamilyFinished = this._onFriendsAndFamilyFinished.bind(this);
    }

    public render(): JSX.Element | null {

        const {
            validationError, fatalError,
            validationForm__ffVerificationFailed
        } = this.props.resources;

        const {
            className,
            description, title,
            logo, emailContact
        } = this.props.config;

        return (
            <div className={`validation-form ${className || ''}`}>
                {logo && <Image
                    {...logo}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={logo.imageSettings}
                />}

                {/* regular login form */}
                {title && <Heading {...title} className='validation-form__marketing-title' />}
                {!this._isFormCompleted && description && <RichTextComponent text={description} className='validation-form__marketing-description' />}
                <div className='validation-form__form'>
                    {!this._isFormCompleted && this._formJSX}
                </div>
                {this._displayValidationError && validationError && <div className='validation-form__error'>{validationError}</div>}
                {this._displayCommunicationsError && fatalError && <div className='validation-form__error'>{fatalError}</div>}
                {/* regular login form end */}

                {/* OR divider */}
                {!this._isFormCompleted &&
                    <div className='validation-form__family-friends-divider'>
                        <span className='validation-form__family-friends-divider-text'>or</span>
                    </div>
                }
                {/* OR divider */}

                {/* FF login */}
                <div className='validation-form__form'>
                    {!this._isFormCompleted && this._familyAndFriendsJSX}
                </div>
                {this._ffDisplayValidationError && validationForm__ffVerificationFailed && <div className='validation-form__error'>{validationForm__ffVerificationFailed} <a href={`mailto:${emailContact}`}>{emailContact}</a></div>}
                {this._ffDisplayCommunicationsError && fatalError && <div className='validation-form__error'>{fatalError}</div>}
                {/* FF login */}

                {this._isFormCompleted && this._successJSX}
            </div>
        );
    }

    private get _successJSX(): JSX.Element {
        const { welcomeMessage } = this.props.config;
        return (
            <React.Fragment>
                {welcomeMessage && <RichTextComponent text={welcomeMessage} className='validation-form__welcome' />}
            </React.Fragment>
        );
    }

    private get _familyAndFriendsJSX(): JSX.Element {
        const {
            validationForm__ffValidation,
            validationForm__ffValidationRequiredError
        } = this.props.resources;

        return (
            <FormInputGroupBuilder
                onFinish={this._onFriendsAndFamilyFinished}
                form={
                    {
                        validationCode: {
                            validity: true,
                            value: '',
                            externalGroup: {
                                labelText: validationForm__ffValidation,
                                validationText: validationForm__ffValidationRequiredError
                            }
                        }
                    }
                }
                submitText={'ENTER'}
            />
        );
    }

    private get _formJSX(): JSX.Element {

        const {
            employeeNumber, employeeNumberValidation,
            birthYear, birthYearValidation,
            lastName, lastNameValidation
        } = this.props.resources;

        return (
            <FormInputGroupBuilder
                onFinish={this._onFinished}
                form={{
                    employeeNumber: {
                        validity: false,
                        value: '',
                        externalGroup: {
                            labelText: employeeNumber,
                            validationText: employeeNumberValidation
                        }
                    },
                    birthYear: {
                        validity: false,
                        value: '',
                        externalGroup: {
                            labelText: birthYear,
                            validationText: birthYearValidation
                        },
                        htmlProps: {
                            placeholder: 'YYYY'
                        }
                    },
                    lastName: {
                        validity: false,
                        value: '',
                        externalGroup: {
                            labelText: lastName,
                            validationText: lastNameValidation
                        }
                    }
                }}
                submitText={'ENTER'}
            />
        );
    }

    private _onFriendsAndFamilyFinished(formData: { validationCode?: string }): void {
        if (formData.validationCode) {
            const { redirectDelay, redirectLink } = this.props.config;

            const input = createValidateFriendsAndFamilyInput(
                this.props.context.actionContext,
                {
                    code: formData.validationCode,
                    emailListId: this.props.context.app.config.mailingListID || '',
                    sourceId: this.props.context.app.config.mailingListSourceID || '' // possible to hardcode to WEB for now
                }
            );
            validateFriendsAndFamilyAction(input, this.props.context.actionContext)
                .then(requestFullfilled => {
                    if (requestFullfilled.Status === 'SUCCESS') {
                        this._isFormCompleted = true;
                        this._ffDisplayCommunicationsError = false;
                        this._ffDisplayValidationError = false;
                        setTimeout(
                            () => {
                                window.location.replace(redirectLink.destinationUrl);
                            },
                            redirectDelay * 1000
                        );
                    } else {
                        this._ffDisplayValidationError = true;
                        this._isFormCompleted = false;
                        this._ffDisplayCommunicationsError = false;
                    }
                })
                .catch(e => {
                    this._ffDisplayCommunicationsError = true;
                    this._isFormCompleted = false;
                    this._ffDisplayValidationError = false;
                });
        }
    }

    private _onFinished(formData: validationFormData): void {
        // process form here
        const { redirectDelay, redirectLink } = this.props.config;

        const input = createEmployeeValidationInput(
            {
                customerId: this.props.context.request.user.customerAccountNumber /* will exist at this stage always */ || 'CUST006052',
                employeeId: this._parseLeadingZeros(formData.employeeNumber),
                birthYear: formData.birthYear,
                lastName: formData.lastName
            },
            this.props.context
        );

        employeeValidationAction(input, this.props.context.actionContext)
            .then(requestFullfilled => {
                // having the set the opposite completion booleans like this
                // shouldn't be necessary but just in case
                if ((requestFullfilled as unknown as { Result: boolean }).Result) {
                    this._isFormCompleted = true;
                    this._displayValidationError = false;
                    this._displayCommunicationsError = false;
                    setTimeout(
                        () => {
                            window.location.replace(redirectLink.destinationUrl);
                        },
                        redirectDelay * 1000
                    );
                } else {
                    this._displayValidationError = true;
                    this._isFormCompleted = false;
                    this._displayCommunicationsError = false;
                }
            })
            .catch(e => {
                // this shouldn't happen, all error codes are set to 200 anyway
                console.error(e);
                this._displayCommunicationsError = true;
                this._isFormCompleted = false;
                this._displayValidationError = false;
            });
    }

    private _parseLeadingZeros(employeeNumber: string): string {
        return employeeNumber.replace(/^0+/, '');
    }
}

export default ValidationForm;
